import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import Header from '@/components/Header';
import { useLanguage } from '@/contexts/LanguageContext';
import { whistleblowerCopy, type PolicyBlock } from '@/content/whistleblower';

const REPORT_EMAIL = 'whistle.blower@tataautocomp.com';

const contacts = [
  {
    name: 'Varsha Purandare',
    email: 'varsha0712@outlook.com',
    roleKey: 'auditRole' as const,
  },
  {
    name: 'Sudipta Marjit',
    email: 'Sudipta.marjit@tataautocomp.com',
    roleKey: 'ethicsRole' as const,
  },
];

const Block = ({ block }: { block: PolicyBlock }) => {
  if (block.kind === 'quote') {
    return (
      <blockquote className="border-l-2 border-secondary pl-5 py-1 text-primary font-display text-lg leading-relaxed">
        {block.text}
      </blockquote>
    );
  }

  if (block.kind === 'list') {
    return (
      <ul className="space-y-3">
        {block.items.map((item) => (
          <li key={item} className="flex gap-3 text-foreground/80 leading-relaxed">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (block.kind === 'defs') {
    return (
      <dl className="divide-y divide-[hsl(var(--hairline))] border-y border-[hsl(var(--hairline))]">
        {block.items.map((item) => (
          <div key={item.term} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-4">
            <dt className="sm:col-span-4 font-display font-semibold text-primary">{item.term}</dt>
            <dd className="sm:col-span-8 text-foreground/80 leading-relaxed">{item.text}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return <p className="text-foreground/80 leading-relaxed">{block.text}</p>;
};

const WhistleBlower = () => {
  const { language } = useLanguage();
  const t = whistleblowerCopy[language];

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content') ?? '';

    document.title = t.title;
    document.documentElement.lang = language;
    description?.setAttribute('content', t.description);

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
      description?.setAttribute('content', previousDescription);
    };
  }, [language, t.title, t.description]);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="relative overflow-hidden surface-dark grain">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
            backgroundSize: '96px 96px',
          }}
          aria-hidden
        />
        <div className="container-tight relative z-10 pt-28 pb-16 md:pt-36 md:pb-24">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow text-secondary mb-6 block">{t.badge}</span>
            <h1
              className="font-display font-bold text-white max-w-4xl"
              style={{ fontSize: 'clamp(2.4rem, 6vw, 4.75rem)', lineHeight: 0.98, letterSpacing: '-0.035em' }}
            >
              {t.headline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/75 leading-relaxed">{t.lede}</p>
            <dl className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
              {t.meta.map((item) => (
                <div key={item.label}>
                  <dt className="font-display text-[11px] uppercase tracking-[0.18em] text-white/45 mb-1">{item.label}</dt>
                  <dd className="font-display text-sm text-white">{item.value}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </div>
      </section>

      <section id="oznamenie" className="scroll-mt-20 border-b border-[hsl(var(--hairline))] bg-muted/40">
        <div className="container-tight py-12 md:py-16">
          <div className="max-w-2xl mb-8">
            <h2 className="text-primary mb-3" style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.1rem)' }}>
              {t.reportTitle}
            </h2>
            <p className="text-foreground/75 leading-relaxed">{t.reportText}</p>
          </div>

          <a
            href={`mailto:${REPORT_EMAIL}`}
            className="group mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl border border-[hsl(var(--hairline))] bg-background px-6 py-5 shadow-[var(--shadow-card)] hover:border-secondary transition-colors"
          >
            <span className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Mail className="h-4 w-4" />
              </span>
              <span>
                <span className="block font-display text-xs uppercase tracking-[0.16em] text-muted-foreground">
                  {t.reportEmailLabel}
                </span>
                <span className="font-display text-lg font-semibold text-primary">{REPORT_EMAIL}</span>
              </span>
            </span>
          </a>

          <p className="mb-6 text-sm text-foreground/70">{t.financeNote}</p>

          <h3 className="font-display text-sm uppercase tracking-[0.16em] text-muted-foreground mb-4">
            {t.contactsTitle}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contacts.map((person) => (
              <article
                key={person.email}
                className="rounded-xl border border-[hsl(var(--hairline))] bg-background p-6 shadow-[var(--shadow-card)]"
              >
                <p className="font-display text-xs uppercase tracking-[0.16em] text-secondary mb-2">
                  {t[person.roleKey]}
                </p>
                <h3 className="font-display text-xl font-semibold text-primary mb-3">{person.name}</h3>
                <p className="text-sm text-foreground/75 leading-relaxed mb-4">
                  TATA AutoComp Systems
                  <br />
                  TACO House, Damle Path
                  <br />
                  Off Law College Road
                  <br />
                  Pune 411004
                </p>
                <a href={`mailto:${person.email}`} className="font-display text-sm text-primary hover:text-secondary transition-colors">
                  {person.email}
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="container-tight py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <nav className="lg:col-span-3" aria-label={t.contents}>
            <div className="lg:sticky lg:top-24">
              <p className="font-display text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{t.contents}</p>
              <ol className="space-y-1">
                {t.sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="flex gap-3 rounded-md px-2 py-1.5 text-sm text-foreground/75 hover:bg-muted hover:text-primary transition-colors"
                    >
                      <span className="font-display tabular-nums text-muted-foreground">{section.num}</span>
                      <span>{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>

          <article className="lg:col-span-9 space-y-14">
            {t.sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-24">
                <span className="section-num mb-3 block">{section.num}</span>
                <h2 className="text-primary mb-6" style={{ fontSize: 'clamp(1.6rem, 2.4vw, 2.15rem)' }}>
                  {section.title}
                </h2>
                <div className="space-y-4 max-w-3xl">{section.blocks.map((block, index) => <Block key={index} block={block} />)}</div>
              </section>
            ))}

            <footer className="border-t border-[hsl(var(--hairline))] pt-8 max-w-3xl">
              <p className="font-display text-lg font-semibold text-primary">{t.signatureName}</p>
              <p className="text-sm text-muted-foreground mt-1">{t.signatureRole}</p>
            </footer>
          </article>
        </div>
      </div>
    </div>
  );
};

export default WhistleBlower;
